import {
  Box,
  Button,
  IconButton,
  RadioGroup,
  Typography,
  FormControlLabel,
  Radio,
} from '@mui/material';
import React, { RefObject, useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AcceptIcon from '../../../assets/icons/accept-icon';
import CancelIcon from '../../../assets/icons/cancel-icon';
import { RetrieveConfigFilesLibrary } from '../../../entities/config-files-library-v1/retrieve-config-files-library';
import ConfigurationFilesLibrary from '../../../services/config-files-library';
import { AppState } from '../../../store';
import { RemoteFlashActionNames } from '../../../store/configure/remote-flash/types';
import { setModalOpen } from '../../../store/modal/actions';
import { enqueueSnackbarAction } from '../../../store/snackbar/actions';
import {
  removeSelectedRows,
  uploadSoftwareFiles,
} from '../../../store/software-library-management/actions';
import {
  LibraryManagementActionNames,
  StandardParameterConfigRecord,
} from '../../../store/software-library-management/types';
import Table from '../../bulk-device-management/table';
import FileUploader from '../../file-loader';
import { tableHeadCells, headCellsAccessors } from './head-cells';
import useStyles from './styles';

const StandardParameterConfigSection = () => {
  const [columnVisibility, setColumnVisibility] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [fileObject, setFileObject] = useState<FileList | null>();
  const [isUploadFile, setIsUploadFile] = useState<Boolean>(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [fileTypeId, setFileTypeId] = useState<string>('10330');
  const tableData = useSelector(
    (state: AppState) => state.libraryManagement.standardParameterConfig,
  );
  const { client, envVariables } = useSelector((state: AppState) => state.login);
  const tableContainer: RefObject<HTMLDivElement> = useRef(null);
  const classes = useStyles();
  const dispatch = useDispatch();

  const TableData = {
    title: 'Standard Parameter Config',
    storageString: 'library-management-standard-parameter-config',
    headerColumns: tableHeadCells,
    sortableAccessors: headCellsAccessors,
    onRowSelection: setSelectedRows,
    data: tableData,
    id: 'software-files',
    setColumnVisibility,
    columnVisibility,
    setColumnOrder,
    columnOrder,
    tableContainer,
  };

  const fileTypes = [
    { id: '10001', label: 'Standard Parameter Config File' },
    { id: '10330', label: 'Single Config File' },
    { id: '10544', label: 'Radio Parameter Config File' },
  ];

  const handleFile = (data: any) => setFileObject(data);
  const clearFile = () => {
    setFileObject(null);
    setIsUploadFile(false);
  };

  const setTableData = useCallback((data: any) => {
    dispatch({
      type: LibraryManagementActionNames.SET_STANDARD_PARAMETER_CONFIG_FILES,
      payload: data,
    });
  }, []);

  const uploadFile = async () => {
    const responses = await Promise.all(
      [...(fileObject || [])].map(
        (file: File) =>
          new Promise((resolve) => {
            const uploadConfig = {
              file,
              fileTypeId,
              partNumber: '',
              overwrite: false,
              setPercentUpload: () => {},
              finishedCallback: (response: StandardParameterConfigRecord) => {
                resolve(response);
                dispatch(
                  enqueueSnackbarAction({
                    message: `${
                      fileTypes.find((fileType) => fileType.id === fileTypeId)?.label
                    } upload successful`,
                    options: {
                      variant: 'success',
                    },
                  }),
                );
              },
              overwriteCallback: () => dispatch(setModalOpen({
                open: true,
                message: `A file with identical contents named ${file.name} already exists in the database, would you like to overwrite the file?`,
                accept: () => {
                  dispatch(uploadSoftwareFiles({    
                    ...uploadConfig, 
                    overwrite: true
                  }));
                  dispatch({
                    type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
                    payload: false,
                  });
              },
                cancel: () => {
                  dispatch({
                    type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
                    payload: false,
                  })
                }
              }))
            };
            dispatch(uploadSoftwareFiles(uploadConfig));
          }),
      ),
    );
    setTableData([
      ...responses,
      ...tableData.filter(data => !responses.some((response: any) => response.id === data.id))
    ]);
    clearFile();
  };
  const handleDeleteSelected = () => {
    dispatch(
      removeSelectedRows(tableData, selectedRows, (deletedIds: Array<string>) => {
        const filteredRows = tableData.filter((row) => !deletedIds.includes(row.id));
        dispatch({
          type: LibraryManagementActionNames.SET_STANDARD_PARAMETER_CONFIG_FILES,
          payload: filteredRows,
        });
      }),
    );
  };

  const getStandardParamterConfigFiles = useCallback(async () => {
    const configFilesLibrary = new ConfigurationFilesLibrary(
      client,
      envVariables.configurationFilesLibraryV1BaseUrl,
    );
    let configFilesLibraries: RetrieveConfigFilesLibrary[] = []
    await Promise.allSettled([
      configFilesLibrary.getConfigFiles({ fileTypeId: '10544', isRetrievable: true }),
      configFilesLibrary.getConfigFiles({ fileTypeId: '10330', isRetrievable: true }),
      configFilesLibrary.getConfigFiles({ fileTypeId: '10001', isRetrievable: true }),
    ]).then(results=>{
      results.forEach(result=>{
        if(result.status==='fulfilled'){ 
          const configFilesResponse = result.value.data?.configs || []
          configFilesLibraries = [...configFilesLibraries, ...configFilesResponse]
        }
      })
      return true
    });
    
    if (configFilesLibraries.length!==0) {
      setTableData(
        configFilesLibraries.map((configFile) => ({
          ...configFile,
          userId: configFile.lastUpdatedByUserId,
        })),
      );
    } else {
      setTableData(null);
    }
  }, [client, envVariables.configurationFilesLibraryV1BaseUrl, setTableData]);

  useEffect(() => {
    getStandardParamterConfigFiles();
  }, []);

  return (
    <div className={classes.scrollBox}>
      <div className={classes.actionRow}>
        <FileUploader
          text="Choose File"
          buttonId="config-files-upload"
          handleFile={handleFile}
          multiple
        />
        <Button
          className={classes.confirm}
          variant="outlined"
          size="small"
          onClick={() => setIsUploadFile(true)}
          disabled={!fileObject}
        >
          Upload File
        </Button>
        {fileObject && isUploadFile && (
          <Box className={classes.confirm} display="flex" alignItems="center">
            <Typography>Are you sure?</Typography>
            <IconButton id={`${TableData.id}-confirm`} onClick={() => uploadFile()} size="large">
              <AcceptIcon />
            </IconButton>
            <IconButton
              id={`${TableData.id}-deny`}
              onClick={() => clearFile()}
              aria-label="edit"
              size="large">
              <CancelIcon />
            </IconButton>
          </Box>
        )}
        <Box ml={1} mr={1}>
          <RadioGroup
            row
            value={fileTypeId}
            onChange={(event) => setFileTypeId(event.target.value)}
          >
            {fileTypes.map((fileType) => (
              <FormControlLabel value={fileType.id} control={<Radio />} label={fileType.label} />
            ))}
          </RadioGroup>
        </Box>
      </div>
      <Box mt={1}>{[...(fileObject || [])].map((file) => file.name).join(', ')}</Box>
      <Box mt={3} mb={3}>
        <Typography variant="h6">Standard Parameter Config File Repository</Typography>
      </Box>
      <Box>
        <Button
          className={classes.button}
          variant="outlined"
          size="small"
          onClick={() => handleDeleteSelected()}
          disabled={selectedRows.length === 0}
        >
          Delete Selected
        </Button>
      </Box>
      <div ref={tableContainer} className={classes.tableContainer}>
        <Table {...TableData} />
      </div>
    </div>
  );
};

export default StandardParameterConfigSection;
